import { useState } from "react";
import { Database, Search, Plus, Upload, Pencil, Trash2, Loader2, X, Tags } from "lucide-react";
import {
  useGetDatabankItemsQuery,
  useGetDatabankFiltersQuery,
  useCreateDatabankItemMutation,
  useUpdateDatabankItemMutation,
  useDeleteDatabankItemMutation,
  useImportDatabankMutation,
  useGetDatabankCategoriesQuery,
} from "../api/databankApi";
import { useGetStatesQuery } from "../../settings/api/settingsApi";
import CategoriesManager from "./CategoriesManager";

const naira = (n: any) => `₦${Number(n || 0).toLocaleString("en-NG")}`;
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

const emptyFilters = { state_id: "", category_id: "", sub_category_id: "", month: "", year: "", q: "" };
const emptyForm = { state_id: "", description: "", unit: "", category_id: "", sub_category_id: "", month: "", year: String(new Date().getFullYear()), price: "" };

function extractError(err: any) {
  return err?.data?.message || err?.data?.errors?.[Object.keys(err?.data?.errors ?? {})[0]]?.[0] || "Something went wrong";
}

export default function AdminDatabankPage() {
  const [filters, setFilters] = useState({ ...emptyFilters });
  const [page, setPage] = useState(1);
  const setF = (k: keyof typeof emptyFilters) => (e: any) => { setFilters((p) => ({ ...p, [k]: e.target.value })); setPage(1); };

  const { data: meta } = useGetDatabankFiltersQuery({ admin: true });
  const { data: allStates = [] } = useGetStatesQuery();
  const { data: categoriesData } = useGetDatabankCategoriesQuery({ admin: true });
  const categories: any[] = Array.isArray(categoriesData)
    ? categoriesData
    : ((categoriesData as any)?.data ?? []);
  const [showCategories, setShowCategories] = useState(false);
  const subOptions = (categoryId: string) =>
    categories.find((c) => String(c.id) === String(categoryId))?.sub_categories ?? [];
  const { data, isLoading, isFetching } = useGetDatabankItemsQuery({ admin: true, ...filters, page, per_page: 50 });

  const [createItem, { isLoading: creating }] = useCreateDatabankItemMutation();
  const [updateItem, { isLoading: updating }] = useUpdateDatabankItemMutation();
  const [deleteItem] = useDeleteDatabankItemMutation();
  const [importCsv, { isLoading: importing }] = useImportDatabankMutation();

  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [formError, setFormError] = useState("");
  const [banner, setBanner] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);

  const items: any[] = data?.data ?? [];
  const total = data?.total ?? 0;
  const lastPage = data?.last_page ?? 1;

  const set = (k: keyof typeof emptyForm) => (e: any) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const openCreate = () => { setEditId(null); setForm({ ...emptyForm }); setFormError(""); setShowForm(true); };
  const openEdit = (it: any) => {
    setEditId(it.id);
    setForm({
      state_id: String(it.state_id ?? ""), description: it.description ?? "", unit: it.unit ?? "",
      category_id: String(it.category_id ?? ""), sub_category_id: String(it.sub_category_id ?? ""),
      month: it.month ?? "", year: String(it.year ?? ""), price: String(it.price ?? ""),
    });
    setFormError(""); setShowForm(true);
  };

  const submit = async () => {
    setFormError("");
    if (!form.state_id || !form.category_id || !form.description.trim() || !form.year || form.price === "") {
      setFormError("State, category, description, year and price are required.");
      return;
    }
    const body: any = {
      ...form,
      state_id: Number(form.state_id),
      category_id: Number(form.category_id),
      sub_category_id: form.sub_category_id ? Number(form.sub_category_id) : null,
      year: Number(form.year),
      price: Number(form.price),
    };
    try {
      if (editId) { await updateItem({ id: editId, body }).unwrap(); setBanner({ type: "success", text: "Item updated." }); }
      else { await createItem(body).unwrap(); setBanner({ type: "success", text: "Item added." }); }
      setShowForm(false);
    } catch (err) { setFormError(extractError(err)); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try { await deleteItem(deleteTarget.id).unwrap(); setBanner({ type: "success", text: "Item deleted." }); }
    catch (err) { setBanner({ type: "error", text: extractError(err) }); }
    setDeleteTarget(null);
  };

  const onImport = async (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res: any = await importCsv(fd).unwrap();
      setBanner({ type: "success", text: res?.message || "Imported." });
    } catch (err) { setBanner({ type: "error", text: extractError(err) }); }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-5">
      {/* Header */}
      <div className="bg-white border rounded-2xl p-5 shadow-sm">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-indigo-50 border border-indigo-100"><Database className="w-5 h-5 text-indigo-600" /></div>
            <div>
              <h1 className="font-bold text-gray-900">Databank — Manage Prices</h1>
              <p className="text-sm text-gray-500">Maintain material/item prices per location and period.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowCategories(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border bg-white hover:bg-gray-50 text-sm">
              <Tags className="w-4 h-4" /> Categories
            </button>
            <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border bg-white hover:bg-gray-50 text-sm cursor-pointer">
              {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Import CSV / Excel
              <input
                type="file"
                accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) onImport(f); e.currentTarget.value = ""; }}
              />
            </label>
            <button onClick={openCreate} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800 text-sm font-medium">
              <Plus className="w-4 h-4" /> Add Item
            </button>
          </div>
        </div>

        <p className="mt-3 text-[11px] text-gray-400">
          CSV / Excel header: <span className="font-mono">location,description,unit,month,year,price,category,sub_category</span>
          {" "}— <span className="font-semibold">location</span> must be a state name (unknown states skipped); categories &amp; sub-categories are created automatically on import.
        </p>

        {/* Filters */}
        <div className="mt-3 grid grid-cols-2 md:grid-cols-6 gap-2">
          <div className="col-span-2 relative">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
            <input value={filters.q} onChange={setF("q")} placeholder="Search…" className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm" />
          </div>
          <select value={filters.state_id} onChange={setF("state_id")} className="border rounded-lg px-2 py-2 text-sm bg-white">
            <option value="">All states</option>{(meta?.states ?? []).map((s) => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
          </select>
          <select
            value={filters.category_id}
            onChange={(e) => { setFilters((p) => ({ ...p, category_id: e.target.value, sub_category_id: "" })); setPage(1); }}
            className="border rounded-lg px-2 py-2 text-sm bg-white"
          >
            <option value="">All categories</option>{(meta?.categories ?? []).map((c) => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
          </select>
          <select value={filters.sub_category_id} onChange={setF("sub_category_id")} disabled={!filters.category_id} className="border rounded-lg px-2 py-2 text-sm bg-white disabled:opacity-50">
            <option value="">All sub-categories</option>
            {((meta?.categories ?? []).find((c) => String(c.id) === filters.category_id)?.sub_categories ?? []).map((s) => (
              <option key={s.id} value={String(s.id)}>{s.name}</option>
            ))}
          </select>
          <select value={filters.month} onChange={setF("month")} className="border rounded-lg px-2 py-2 text-sm bg-white">
            <option value="">All months</option>{(meta?.months ?? []).map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <select value={filters.year} onChange={setF("year")} className="border rounded-lg px-2 py-2 text-sm bg-white">
            <option value="">All years</option>{(meta?.years ?? []).map((y) => <option key={y} value={String(y)}>{y}</option>)}
          </select>
        </div>
      </div>

      {banner && (
        <div className={`flex items-center justify-between gap-3 px-4 py-3 rounded-xl text-sm border ${banner.type === "success" ? "bg-green-50 border-green-200 text-green-800" : "bg-rose-50 border-rose-200 text-rose-800"}`}>
          <span>{banner.text}</span>
          <button onClick={() => setBanner(null)}><X className="w-4 h-4 opacity-60" /></button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border rounded-2xl shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="py-20 flex flex-col items-center gap-2 text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /><p className="text-sm">Loading…</p></div>
        ) : items.length === 0 ? (
          <div className="py-20 text-center text-sm text-gray-400">No items. Add one or import a CSV.</div>
        ) : (
          <div>
            <table
              // No sideways scrolling: cells wrap inside their own column
              // instead, which is what `table-fixed` enforces.
              className="w-full table-fixed text-sm break-words"
            >
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                <tr>
                  <th className="text-left font-semibold px-4 py-3">Description</th>
                  <th className="text-left font-semibold px-4 py-3">Unit</th>
                  <th className="text-left font-semibold px-4 py-3">Category</th>
                  <th className="text-left font-semibold px-4 py-3">State</th>
                  <th className="text-left font-semibold px-4 py-3">Period</th>
                  <th className="text-right font-semibold px-4 py-3">Price</th>
                  <th className="text-right font-semibold px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {items.map((it) => (
                  <tr key={it.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-gray-900">{it.description}</div>
                      {it.sub_category?.name && <div className="text-[11px] text-gray-400">{it.sub_category.name}</div>}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{it.unit ?? "—"}</td>
                    <td className="px-4 py-3 text-gray-600">{it.category?.name ?? "—"}</td>
                    <td className="px-4 py-3 text-gray-600">{it.state?.name ?? "—"}</td>
                    <td className="px-4 py-3 text-gray-600">{[it.month, it.year].filter(Boolean).join(" ")}</td>
                    <td className="px-4 py-3 text-right font-black text-gray-900">{naira(it.price)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => openEdit(it)} className="p-1.5 rounded-lg border hover:bg-gray-50 text-gray-500"><Pencil className="w-3.5 h-3.5" /></button>
                        <button onClick={() => setDeleteTarget(it)} className="p-1.5 rounded-lg border hover:bg-rose-50 text-rose-500"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {lastPage > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-40 hover:bg-gray-50">Prev</button>
          <span className="text-sm text-gray-500">Page {page} of {lastPage} {isFetching && "…"}</span>
          <button disabled={page >= lastPage} onClick={() => setPage((p) => Math.min(lastPage, p + 1))} className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-40 hover:bg-gray-50">Next</button>
        </div>
      )}

      {/* Add/Edit modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="font-semibold text-gray-900">{editId ? "Edit Item" : "Add Item"}</h3>
              <button onClick={() => setShowForm(false)}><X className="w-4 h-4 text-gray-500" /></button>
            </div>
            <div className="p-6 grid grid-cols-2 gap-3">
              <Field label="Description *" className="col-span-2"><input className={inp} value={form.description} onChange={set("description")} /></Field>
              <Field label="State *">
                <select className={inp} value={form.state_id} onChange={set("state_id")}>
                  <option value="">Select state…</option>
                  {(allStates as any[]).map((s) => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
                </select>
              </Field>
              <Field label="Unit"><input className={inp} value={form.unit} onChange={set("unit")} placeholder="M3, Bag, Nr…" /></Field>
              <Field label="Category *">
                <select className={inp} value={form.category_id} onChange={(e) => setForm((p) => ({ ...p, category_id: e.target.value, sub_category_id: "" }))}>
                  <option value="">Select category…</option>
                  {categories.map((c) => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
                </select>
              </Field>
              <Field label="Sub-category">
                <select className={inp} value={form.sub_category_id} onChange={set("sub_category_id")} disabled={!form.category_id}>
                  <option value="">—</option>
                  {subOptions(form.category_id).map((s: any) => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
                </select>
              </Field>
              <Field label="Month">
                <select className={inp} value={form.month} onChange={set("month")}>
                  <option value="">—</option>{MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </Field>
              <Field label="Year *"><input type="number" className={inp} value={form.year} onChange={set("year")} /></Field>
              <Field label="Price (₦) *" className="col-span-2"><input type="number" className={inp} value={form.price} onChange={set("price")} placeholder="0" /></Field>
              {formError && <div className="col-span-2 text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">{formError}</div>}
            </div>
            <div className="flex gap-3 px-6 pb-6">
              <button onClick={() => setShowForm(false)} className="flex-1 py-2.5 border rounded-lg text-sm hover:bg-gray-50">Cancel</button>
              <button onClick={submit} disabled={creating || updating} className="flex-1 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-60 flex items-center justify-center gap-2">
                {(creating || updating) && <Loader2 className="w-4 h-4 animate-spin" />} Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <h3 className="font-semibold text-gray-900">Delete item?</h3>
            <p className="text-sm text-gray-500 mt-1">{deleteTarget.description} ({deleteTarget.state?.name ?? "—"}, {deleteTarget.year}) will be removed.</p>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setDeleteTarget(null)} className="flex-1 py-2 border rounded-lg text-sm hover:bg-gray-50">Cancel</button>
              <button onClick={confirmDelete} className="flex-1 py-2 rounded-lg bg-rose-600 text-white text-sm font-medium hover:bg-rose-700">Delete</button>
            </div>
          </div>
        </div>
      )}

      {showCategories && <CategoriesManager categories={categories as any[]} onClose={() => setShowCategories(false)} />}
    </div>
  );
}

const inp = "mt-1 w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-100 bg-white";
function Field({ label, className = "", children }: { label: string; className?: string; children: React.ReactNode }) {
  return (
    <div className={className}>
      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{label}</label>
      {children}
    </div>
  );
}
